import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, RefreshCw, Loader2, Save, Trash2, Grid3X3, Clock } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import toast from 'react-hot-toast';
import { zonesApi, snapshotsApi } from '../../services/api';
import ZoneCanvas from './ZoneCanvas';
import type { Zone, CreateZone, ZoneMeasurementType } from '../../types';

interface ZoneEditorProps {
  cameraId: string;
  cameraName: string;
  isOpen: boolean;
  onClose: () => void;
}

interface LocalZone {
  id?: string;
  isNew?: boolean;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  low_stock_threshold?: number | null;
  measurement_type?: ZoneMeasurementType;
  product_category?: string | null;
  dwell_threshold_seconds?: number | null;
}

export default function ZoneEditor({ cameraId, cameraName, isOpen, onClose }: ZoneEditorProps) {
  const queryClient = useQueryClient();
  const [localZones, setLocalZones] = useState<LocalZone[]>([]);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const [isRequestingSnapshot, setIsRequestingSnapshot] = useState(false);
  const [snapshotRequestTime, setSnapshotRequestTime] = useState<number | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Fetch existing zones
  const { data: existingZones, isLoading: zonesLoading } = useQuery({
    queryKey: ['zones', cameraId],
    queryFn: () => zonesApi.listForCamera(cameraId),
    enabled: isOpen,
  });

  // Fetch snapshot status
  const { data: snapshotStatus } = useQuery({
    queryKey: ['snapshot-status', cameraId],
    queryFn: () => snapshotsApi.getStatus(cameraId),
    enabled: isOpen,
    refetchInterval: isRequestingSnapshot ? 2000 : false,
  });

  // Fetch current zone counts (poll every 5 seconds for live updates)
  const { data: zoneCounts } = useQuery({
    queryKey: ['zone-counts', cameraId],
    queryFn: () => zonesApi.getLatestCounts(cameraId),
    enabled: isOpen,
    refetchInterval: 5000,
  });

  // Convert zone counts to Map for ZoneCanvas
  const zoneCountsMap = zoneCounts
    ? new Map(zoneCounts.map((c) => [c.zone_id, c.count]))
    : undefined;

  // Initialize local zones from existing
  useEffect(() => {
    if (existingZones) {
      setLocalZones(existingZones.map((z) => ({ ...z, isNew: false })));
    }
  }, [existingZones]);

  // Load snapshot when available
  useEffect(() => {
    if (snapshotStatus?.available && !snapshotUrl) {
      loadSnapshot();
    }
    if (snapshotStatus?.available && isRequestingSnapshot) {
      setIsRequestingSnapshot(false);
      setSnapshotRequestTime(null);
      loadSnapshot();
    }
  }, [snapshotStatus]);

  // Timeout for snapshot request (30 seconds)
  useEffect(() => {
    if (!isRequestingSnapshot || !snapshotRequestTime) return;

    const timeout = setTimeout(() => {
      if (isRequestingSnapshot) {
        setIsRequestingSnapshot(false);
        setSnapshotRequestTime(null);
        toast.error('Snapshot request timed out. Camera may be offline.');
      }
    }, 30000);

    return () => clearTimeout(timeout);
  }, [isRequestingSnapshot, snapshotRequestTime]);

  const loadSnapshot = async () => {
    try {
      const blob = await snapshotsApi.get(cameraId);
      const url = URL.createObjectURL(blob);
      setSnapshotUrl(url);
    } catch (err) {
      console.error('Failed to load snapshot:', err);
    }
  };

  // Request new snapshot
  const requestSnapshot = async () => {
    setIsRequestingSnapshot(true);
    setSnapshotRequestTime(Date.now());
    try {
      await snapshotsApi.request(cameraId);
      toast.success('Snapshot requested. Waiting for camera...');
    } catch (err) {
      toast.error('Failed to request snapshot');
      setIsRequestingSnapshot(false);
      setSnapshotRequestTime(null);
    }
  };

  // Create zone mutation
  const createZoneMutation = useMutation({
    mutationFn: (zone: CreateZone) => zonesApi.create(cameraId, zone),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones', cameraId] });
      queryClient.invalidateQueries({ queryKey: ['zones-summary'] });
    },
  });

  // Update zone mutation
  const updateZoneMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateZone> }) => zonesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones', cameraId] });
      queryClient.invalidateQueries({ queryKey: ['zones-summary'] });
    },
  });

  // Delete zone mutation
  const deleteZoneMutation = useMutation({
    mutationFn: (id: string) => zonesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones', cameraId] });
      queryClient.invalidateQueries({ queryKey: ['zones-summary'] });
    },
  });

  // Handle zone creation from canvas
  const handleZoneCreate = useCallback((coords: Omit<CreateZone, 'name'>) => {
    const newZone: LocalZone = {
      ...coords,
      name: `Zone ${localZones.length + 1}`,
      id: `temp-${Date.now()}`,
      isNew: true,
      measurement_type: 'item_count',
    };
    setLocalZones((prev) => [...prev, newZone]);
    setSelectedZoneId(newZone.id!);
    setIsDirty(true);
  }, [localZones.length]);

  // Handle zone update from canvas
  const handleZoneUpdate = useCallback((id: string, updates: Partial<CreateZone>) => {
    setLocalZones((prev) =>
      prev.map((z) => (z.id === id ? { ...z, ...updates } : z))
    );
    setIsDirty(true);
  }, []);

  // Handle zone delete
  const handleZoneDelete = useCallback((id: string) => {
    setLocalZones((prev) => prev.filter((z) => z.id !== id));
    if (selectedZoneId === id) {
      setSelectedZoneId(null);
    }
    setIsDirty(true);
  }, [selectedZoneId]);

  // Get selected zone
  const selectedZone = localZones.find((z) => z.id === selectedZoneId);

  // Update selected zone field
  const updateSelectedZone = (updates: Partial<LocalZone>) => {
    if (!selectedZoneId) return;
    setLocalZones((prev) =>
      prev.map((z) => (z.id === selectedZoneId ? { ...z, ...updates } : z))
    );
    setIsDirty(true);
  };

  // Save all changes
  const handleSave = async () => {
    try {
      // Find zones to create, update, or delete
      const existingIds = new Set(existingZones?.map((z) => z.id) || []);

      // Delete removed zones
      for (const ez of existingZones || []) {
        if (!localZones.find((lz) => lz.id === ez.id)) {
          await deleteZoneMutation.mutateAsync(ez.id);
        }
      }

      // Create or update zones
      for (const lz of localZones) {
        if (lz.isNew || lz.id?.startsWith('temp-')) {
          // Create new zone
          await createZoneMutation.mutateAsync({
            name: lz.name,
            x: lz.x,
            y: lz.y,
            width: lz.width,
            height: lz.height,
            low_stock_threshold: lz.low_stock_threshold,
            measurement_type: lz.measurement_type,
            product_category: lz.product_category,
            dwell_threshold_seconds: lz.dwell_threshold_seconds,
          });
        } else if (existingIds.has(lz.id!)) {
          // Update existing zone
          const original = existingZones?.find((ez) => ez.id === lz.id);
          if (original && hasChanges(original, lz)) {
            await updateZoneMutation.mutateAsync({
              id: lz.id!,
              data: {
                name: lz.name,
                x: lz.x,
                y: lz.y,
                width: lz.width,
                height: lz.height,
                low_stock_threshold: lz.low_stock_threshold,
                measurement_type: lz.measurement_type,
                product_category: lz.product_category,
                dwell_threshold_seconds: lz.dwell_threshold_seconds,
              },
            });
          }
        }
      }

      toast.success('Zones saved successfully');
      setIsDirty(false);
      onClose();
    } catch (err) {
      toast.error('Failed to save zones');
    }
  };

  // Check if zone has changes
  const hasChanges = (original: Zone, local: LocalZone): boolean => {
    return (
      original.name !== local.name ||
      original.x !== local.x ||
      original.y !== local.y ||
      original.width !== local.width ||
      original.height !== local.height ||
      original.low_stock_threshold !== local.low_stock_threshold ||
      original.measurement_type !== local.measurement_type ||
      original.product_category !== local.product_category ||
      original.dwell_threshold_seconds !== local.dwell_threshold_seconds
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-xl font-semibold">Configure Zones</h2>
            <p className="text-sm text-gray-500">{cameraName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          <div className="flex gap-4">
            {/* Canvas Section */}
            <div className="flex-1">
              {zonesLoading ? (
                <div className="flex items-center justify-center h-64 bg-gray-100 rounded-lg">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : (
                <ZoneCanvas
                  snapshotUrl={snapshotUrl}
                  zones={localZones}
                  selectedZoneId={selectedZoneId}
                  zoneCounts={zoneCountsMap}
                  onZoneCreate={handleZoneCreate}
                  onZoneUpdate={handleZoneUpdate}
                  onZoneSelect={setSelectedZoneId}
                  onZoneDelete={handleZoneDelete}
                />
              )}

              {/* Snapshot Controls */}
              <div className="mt-4 flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={requestSnapshot}
                  disabled={isRequestingSnapshot}
                >
                  {isRequestingSnapshot ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  {isRequestingSnapshot ? 'Waiting for camera...' : 'Request New Snapshot'}
                </Button>
                {snapshotStatus?.snapshot_at && (
                  <span className="text-xs text-gray-500">
                    Last snapshot: {new Date(snapshotStatus.snapshot_at).toLocaleString()}
                  </span>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="w-72 space-y-4">
              {/* Zone List */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Zones ({localZones.length})
                </h3>
                <div className="space-y-2 max-h-48 overflow-auto">
                  {localZones.map((zone) => (
                    <button
                      key={zone.id}
                      onClick={() => setSelectedZoneId(zone.id || null)}
                      className={`w-full text-left p-2 rounded-lg border transition-colors ${
                        selectedZoneId === zone.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {zone.measurement_type === 'person_dwell' ? (
                          <Clock className="w-4 h-4 text-purple-500" />
                        ) : (
                          <Grid3X3 className="w-4 h-4 text-blue-500" />
                        )}
                        <span className="font-medium text-sm truncate">{zone.name}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {zone.measurement_type === 'person_dwell' ? 'Dwell Time' : 'Item Count'}
                      </p>
                    </button>
                  ))}
                  {localZones.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Draw on the image to create zones
                    </p>
                  )}
                </div>
              </div>

              {/* Zone Config */}
              {selectedZone && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Zone Settings</h3>
                  <div className="space-y-3">
                    <Input
                      label="Name"
                      value={selectedZone.name}
                      onChange={(e) => updateSelectedZone({ name: e.target.value })}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Measurement Type
                      </label>
                      <select
                        value={selectedZone.measurement_type || 'item_count'}
                        onChange={(e) =>
                          updateSelectedZone({
                            measurement_type: e.target.value as ZoneMeasurementType,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="item_count">Item Counting</option>
                        <option value="person_dwell">Person Dwell Time</option>
                      </select>
                    </div>

                    {selectedZone.measurement_type === 'item_count' && (
                      <>
                        <Input
                          label="Product Category (optional)"
                          value={selectedZone.product_category || ''}
                          onChange={(e) =>
                            updateSelectedZone({
                              product_category: e.target.value || undefined,
                            })
                          }
                          placeholder="e.g., Bread, Electronics"
                        />
                        <Input
                          label="Low Stock Threshold"
                          type="number"
                          value={selectedZone.low_stock_threshold?.toString() || ''}
                          onChange={(e) =>
                            updateSelectedZone({
                              low_stock_threshold: e.target.value
                                ? parseInt(e.target.value)
                                : undefined,
                            })
                          }
                          placeholder="Alert when count falls below"
                        />
                      </>
                    )}

                    {selectedZone.measurement_type === 'person_dwell' && (
                      <Input
                        label="Alert After (seconds)"
                        type="number"
                        value={selectedZone.dwell_threshold_seconds?.toString() || ''}
                        onChange={(e) =>
                          updateSelectedZone({
                            dwell_threshold_seconds: e.target.value
                              ? parseInt(e.target.value)
                              : undefined,
                          })
                        }
                        placeholder="Alert when person stays longer"
                      />
                    )}

                    <Button
                      variant="danger"
                      size="sm"
                      className="w-full"
                      onClick={() => handleZoneDelete(selectedZone.id!)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Zone
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isDirty || createZoneMutation.isPending || updateZoneMutation.isPending}
          >
            {(createZoneMutation.isPending || updateZoneMutation.isPending) ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Zones
          </Button>
        </div>
      </Card>
    </div>
  );
}
